// Apró DOM-segéd.
//
// Keretrendszer helyett ez elég: a tervek statikus HTML-ben készültek, a
// ds.css komponensosztályaival — így a képernyők szinte egy az egyben
// átemelhetők, és a szállított kód is kicsi marad (a Flutter web 5,2 MB-os
// csomagja pont ezért lassú).

type Child = Node | string | null | undefined | false;

type Attrs = {
  class?: string;
  text?: string;
  html?: string;
  /// Képernyőolvasó-név; ikon-gombon az átadási leírás szerint kötelező.
  'aria-label'?: string;
  [key: string]: unknown;
};

/// Elem létrehozása: `h('button', { class: 'btn btn-primary', text: 'Mentés' })`
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);

  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === null || value === false) continue;

    if (key === 'text') {
      el.textContent = String(value);
    } else if (key === 'html') {
      el.innerHTML = String(value);
    } else if (key === 'style' && typeof value === 'object') {
      Object.assign(el.style, value as Partial<CSSStyleDeclaration>);
    } else if (key.startsWith('on') && typeof value === 'function') {
      el.addEventListener(
        key.slice(2).toLowerCase(),
        value as EventListenerOrEventListenerObject,
      );
    } else {
      el.setAttribute(key, String(value));
    }
  }

  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    el.append(typeof child === 'string' ? document.createTextNode(child) : child);
  }

  return el;
}

/// Inline SVG ikon a tervekből. A `currentColor` miatt a szín a szövegtől jön.
export function icon(path: string, size = 16): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.5');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  // Dekoratív: a jelentést a körülötte lévő szöveg hordozza.
  svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML = `<path d="${path}" />`;
  return svg;
}

export function clear(el: HTMLElement): void {
  el.replaceChildren();
}

/// Kis ikonkészlet a tervekből átemelve.
export const icons = {
  spark: 'M8 1.8l1.5 4.2 4.7 1.5-4.7 1.5L8 14.2 6.5 9 1.8 7.5 6.5 6z',
  shield: 'M8 2.2l4.8 1.8v3.6c0 3-2 5.2-4.8 6.2-2.8-1-4.8-3.2-4.8-6.2V4z',
  warning: 'M8 2.6L14.2 13H1.8z',
  eye: 'M1.5 8s2.4-4 6.5-4 6.5 4 6.5 4-2.4 4-6.5 4S1.5 8 1.5 8z',
  comment:
    'M13.4 8.4c0 2.6-2.4 4.6-5.4 4.6-.8 0-1.6-.1-2.3-.4L2.6 13.6l.9-2.6A4.4 4.4 0 012.6 8.4c0-2.6 2.4-4.6 5.4-4.6s5.4 2 5.4 4.6z',
  clip: 'M11.4 6.2l-4.6 4.6a2 2 0 002.8 2.8l4.6-4.6a3.4 3.4 0 00-4.8-4.8L4.2 9.4',
  filter: 'M2.6 3.6h10.8L9.4 8.4v4l-2.8 1.4v-5.4z',
  calendar: 'M2.4 6.6h11.2M5.6 2.2v2.4M10.4 2.2v2.4M4 3.4h8a1.6 1.6 0 011.6 1.6v7a1.6 1.6 0 01-1.6 1.6H4a1.6 1.6 0 01-1.6-1.6V5A1.6 1.6 0 014 3.4z',
  timer: 'M8 4.6V8l2.2 1.4M8 2.4a5.6 5.6 0 100 11.2 5.6 5.6 0 000-11.2z',
  more: 'M4 8h.01M8 8h.01M12 8h.01',
  dot: 'M8 5.4a2.6 2.6 0 100 5.2 2.6 2.6 0 000-5.2z',
  check: 'M3.6 8.4l2.8 2.8 6-6.4',
  arrowRight: 'M3 8h10M9.4 4.6L12.8 8l-3.4 3.4',
  close: 'M4 4l8 8M12 4l-8 8',
  info: 'M8 2.4a5.6 5.6 0 100 11.2 5.6 5.6 0 000-11.2zM8 7.2v3.6M8 5.2v.2',
  plus: 'M8 3.4v9.2M3.4 8h9.2',
  users: 'M6 7.4a2.2 2.2 0 100-4.4 2.2 2.2 0 000 4.4zM2.2 13.4c0-2.1 1.7-3.4 3.8-3.4s3.8 1.3 3.8 3.4M11 7.2a1.8 1.8 0 100-3.6M11.4 10.2c1.4.2 2.4 1.2 2.4 2.8',
  search: 'M7.2 11.6a4.4 4.4 0 100-8.8 4.4 4.4 0 000 8.8zM10.6 10.6L14 14',
  bell: 'M4.4 6.8a3.6 3.6 0 017.2 0c0 3 1.2 4 1.2 4H3.2s1.2-1 1.2-4zM6.6 13.2a1.6 1.6 0 002.8 0',
  chart: 'M2.6 13.4h10.8M4.6 11.4V7M8 11.4V3.4M11.4 11.4V8.6',
  folder: 'M2.4 4.6a1.2 1.2 0 011.2-1.2h2.6l1.4 1.8h4.4a1.2 1.2 0 011.2 1.2v5.4a1.2 1.2 0 01-1.2 1.2H3.6a1.2 1.2 0 01-1.2-1.2z',
  pencil: 'M11.2 2.8l2 2L6 12H4v-2zM10 4l2 2',
  drag: 'M6 4h.01M10 4h.01M6 8h.01M10 8h.01M6 12h.01M10 12h.01',
  download: 'M8 2.6v7.4M5 7.4L8 10.4l3-3M3 12.4h10',
  upload: 'M8 13.4V6M5 9L8 6l3 3M3 3.6h10',
  trash: 'M3.4 4.6h9.2M6.4 4.6V3.4h3.2v1.2M4.6 4.6l.6 8.2h5.6l.6-8.2',
  // A tervek „függő mentés" és „nincs kapcsolat" jelöléséhez (DS04).
  wifiOff:
    'M2 2l12 12M5.2 9.6a4 4 0 014.4-.8M2.6 6.8a8 8 0 013.2-1.9' +
    'M13.4 6.8a8 8 0 00-3.6-2M8 12.4v.2',
} as const;
